"use client";

import type { SectionState } from "../navigation";

/* -------------------------------------------------------------------------- */
/* OrgIssuesBanner                                                             */
/* -------------------------------------------------------------------------- */

export interface OrgIssuesBannerProps {
  /**
   * Array of statuses from all sections.  The banner renders `null` when
   * every status is `"complete"`.
   */
  statuses: { state: SectionState }[];
}

type Severity = "red" | "yellow" | "gray";

function maxSeverity(states: SectionState[]): Severity {
  if (states.some((s) => s === "red")) return "red";
  if (states.some((s) => s === "yellow")) return "yellow";
  return "gray";
}

const BULLET_CLS: Record<Severity, string> = {
  red: "bg-[var(--accent-red,#dc3545)]",
  yellow: "bg-[var(--accent-gold,#f0ad00)]",
  gray: "bg-(--fg-muted)",
};

const WRAPPER_CLS: Record<Severity, string> = {
  red: "border-[color-mix(in_oklab,var(--accent-red,#dc3545)_35%,transparent)] bg-[color-mix(in_oklab,var(--accent-red,#dc3545)_6%,transparent)]",
  yellow:
    "border-[color-mix(in_oklab,var(--accent-gold,#f0ad00)_35%,transparent)] bg-[color-mix(in_oklab,var(--accent-gold,#f0ad00)_6%,transparent)]",
  gray: "border-(--border-default) bg-(--bg-canvas)",
};

/**
 * Banner that summarises section-level validation issues by severity.
 * Returns `null` when all statuses are `"complete"`.
 */
export function OrgIssuesBanner({ statuses }: OrgIssuesBannerProps) {
  const issues = statuses.filter((s) => s.state !== "complete");
  if (issues.length === 0) return null;

  const severity = maxSeverity(issues.map((i) => i.state));

  const counts = {
    red: issues.filter((i) => i.state === "red").length,
    yellow: issues.filter((i) => i.state === "yellow").length,
    gray: issues.filter((i) => i.state === "gray").length,
  };

  const lines: { severity: Severity; text: string }[] = [];
  if (counts.red > 0) {
    const n = counts.red;
    lines.push({
      severity: "red",
      text: `${n} ${n === 1 ? "section" : "sections"} with field errors`,
    });
  }
  if (counts.yellow > 0) {
    const n = counts.yellow;
    lines.push({
      severity: "yellow",
      text: `${n} ${n === 1 ? "section" : "sections"} affecting existing opportunities`,
    });
  }
  if (counts.gray > 0) {
    const n = counts.gray;
    lines.push({
      severity: "gray",
      text: `${n} ${n === 1 ? "section" : "sections"} with missing data`,
    });
  }

  return (
    <div
      role="note"
      className={["rounded-[6px] border px-4 py-3", WRAPPER_CLS[severity]].join(
        " ",
      )}
    >
      <ul className="flex flex-col gap-1">
        {lines.map((line) => (
          <li
            key={line.severity}
            className="flex items-center gap-2 text-sm text-(--fg-secondary)"
          >
            <span
              aria-hidden
              className={[
                "w-[6px] h-[6px] rounded-full shrink-0",
                BULLET_CLS[line.severity],
              ].join(" ")}
            />
            {line.text}
          </li>
        ))}
      </ul>
    </div>
  );
}
